/**
 * Self-contained types mirroring the shape of the GraphQL-generated `Event` data this
 * library operates on. Kept independent of `../../gql/graphql` (string-literal unions
 * instead of generated enums, plain object types instead of generated object types) so
 * this library has no dependency on any GraphQL codegen output and can be shared
 * between `mini/` and `web/`. Real generated data structurally satisfies these types
 * without a cast; only code that forwards a field read back out of this library's
 * types into a parameter still typed with the real generated enum needs a matching
 * widening on that parameter (see mini/src/lib/event/README.md).
 */

export type GameFormat = 'FREEPLAY' | 'SINGLES' | 'DOUBLES' | 'ROTATING_DOUBLES'
export const GameFormat = {
  Freeplay: 'FREEPLAY',
  Singles: 'SINGLES',
  Doubles: 'DOUBLES',
  RotatingDoubles: 'ROTATING_DOUBLES',
} as const satisfies Record<string, GameFormat>

export type SportCategory = 'TENNIS' | 'PICKLEBALL'
export type Gender = 'FEMALE' | 'MALE' | 'OTHER'

export type PlayerUser = {
  __typename?: 'UserBasic'
  id?: string | null
  openid: string
  name?: string | null
  avatar?: string | null
  mobile?: string | null
  gender?: Gender | null
  ratings: { __typename?: 'UserRating'; sport: SportCategory; rating?: number | null }[]
}

export type EventCompetitor = {
  __typename?: 'EventCompetitor'
  id: string
  rank?: number | null
  player1: PlayerUser
  player2?: PlayerUser | null
}

export type MatchSet = {
  __typename?: 'MatchSet'
  side1Score: number
  side2Score: number
  side1TiebreakPoints?: number | null
  side2TiebreakPoints?: number | null
}

// A match record's side, as read from the backend (a list of players plus how many
// sets that side won). Named distinctly from `MatchSide` below, which is an unrelated
// planner concept (a seeding number or list of seeding numbers).
export type EventMatchSide = {
  __typename?: 'MatchSide'
  setsWon: number
  players: PlayerUser[]
}

export type EventMatch = {
  __typename?: 'Match'
  id: string
  eventId: string
  round: number
  sides: EventMatchSide[]
  sets: MatchSet[]
}

export type Event = {
  __typename?: 'Event'
  id: string
  creatorId: string
  siteId?: string | null
  gameFormat: GameFormat
  sport: SportCategory
  coverImages?: string[] | null
  scoreUpdatedAt?: string | null
  site?: {
    images?: string[] | null
    name: string
    avatar?: string | null
    location: string
  } | null
  signups: {
    id: string
    signupTime?: string | null
    orderId?: string | null
    user: PlayerUser
  }[]
  competitors: EventCompetitor[]
  matches: EventMatch[]
}

export type SeedingNumber = number

export type PlannerEvent = Pick<Event, 'id'> & {
  gameFormat: GameFormat
  competitors: Pick<EventCompetitor, 'id' | 'player1' | 'player2'>[]
  matches: Pick<EventMatch, 'id' | 'eventId' | 'round' | 'sides' | 'sets'>[]
}

/**
 * Each side is either a single seeding (singles/australian-solo) or an array of seedings (doubles team).
 * [side1, side2] where side = SeedingNumber | SeedingNumber[]
 */
export type MatchSide = SeedingNumber | SeedingNumber[]
export type MatchEntry = [MatchSide, MatchSide]

// Convenience aliases kept for the planner's static plan arrays
export type SinglesMatchEntry = [SeedingNumber, SeedingNumber]
export type DoublesMatchEntry = [
  [SeedingNumber, SeedingNumber],
  [SeedingNumber, SeedingNumber],
]
export type AustralianDoublesMatchEntry = [
  SeedingNumber,
  [SeedingNumber, SeedingNumber],
]

export type Round = MatchEntry[]
export type Stage = Round[]
